/**
 * Workflow types (Stage 1 + Stage 4)
 *
 * Workflow objects are stored per workspace. A cycle may reference a workflow
 * explicitly, or inherit the workspace default, or use the canonical fallback.
 *
 * A Workflow is a runtime orchestration policy — always live throughout the
 * lifecycle of every cycle that uses it. It governs:
 *   - allowed Session context modes
 *   - write-back behavior (attribute, result, governance)
 *   - result resolution strategy
 *   - catchball enablement
 *   - cascade session defaults (timing mode, entry mode, facilitator)
 *
 * A Workflow may optionally reference a CycleTemplate (template_id).
 * The template is applied once at cycle creation; the workflow continues to
 * govern the cycle afterward. See cycleTemplate.ts.
 *
 * Canon: "Workflow is always live. Templates are applied once."
 */

export type WorkflowStatus = 'draft' | 'active' | 'archived';

/** Session context modes that a workflow can allow. Doc 04 §10. */
export type AllowedSessionMode = 'cycle_linked' | 'app_linked' | 'isolated';

/** Attribute write-back policy. Doc 04 §12. */
export type AttributeWriteBack = 'none' | 'session_only' | 'direct';

/** Result write-back policy. Doc 04 §12. */
export type ResultWriteBack = 'none' | 'session_only' | 'to_track';

/** Governance/proposal write-back. Doc 04 §12. */
export type GovernanceWriteBack = 'none' | 'proposal_eligible';

/** Result resolution strategy for tracks. Doc 04 §13.1. */
export type ResultResolutionStrategy = 'latest_published' | 'aggregate' | 'selected_official';

/** Session context mode (stored on session). Doc 04 §11. */
export type SessionContextMode = 'cycle_linked' | 'app_linked' | 'isolated';

/**
 * Every session context mode, in the order the session-start chooser shows
 * them. The one source for "all modes" (S-21, owner ruling 2026-10-14): a
 * workflow with no allowed_session_modes allows these.
 */
export const SESSION_CONTEXT_MODES: readonly SessionContextMode[] = ['cycle_linked', 'app_linked', 'isolated'];

/** Plain labels for the chooser (S-21). */
export const SESSION_CONTEXT_MODE_LABELS: Record<SessionContextMode, string> = {
  cycle_linked: 'Cycle',
  app_linked: 'App',
  isolated: 'Stand-alone',
};

// ---------------------------------------------------------------------------
// Workflow template preset types — DEPRECATED in WorkflowConfiguration.
// Use CycleTemplate (cycleTemplate.ts) for new code. These aliases remain
// so existing data stored in workflow.configuration JSONB is still readable
// without a data migration.
// ---------------------------------------------------------------------------

/** @deprecated Use TemplateAttributePreset from cycleTemplate.ts instead. */
export interface WorkflowAttributePreset {
  attributeKey: string;
  scaleId?: string | null;
  required?: boolean;
  defaultRange?: WorkflowRangePreset | null;
}

// ---------------------------------------------------------------------------
// Workflow-level criteria governance
// ---------------------------------------------------------------------------

/**
 * A single attribute entry in the workflow's locked criteria set.
 *
 * When `required` is true, every cycle using this workflow MUST include this
 * attribute — it cannot be dropped via updateCriteria or deleteCycleConfig.
 *
 * When `lockedRange` is present, cycles cannot propose a different value range
 * for this attribute. Any `proposeRange` call with a mismatching range is
 * rejected. This closes the cross-cycle range-drift problem.
 */
export interface WorkflowLockedAttribute {
  /** Matches the attributeKey used throughout cycle_attribute_configs. */
  attributeKey: string;
  /** When true, this criterion cannot be removed from any cycle that uses this workflow. */
  required: boolean;
  /**
   * Canonical range definition. When present, cycles must use this range exactly.
   * proposedRange calls that deviate from this value are rejected at the store layer.
   * null = attribute is required but range can be set per-cycle.
   */
  lockedRange?: WorkflowRangePreset | null;
}

/**
 * How criteria and value ranges are governed relative to the workflow.
 *
 * 'workflow_defined' — the workflow owns the criteria set and (optionally)
 *   canonical ranges. Cycles inherit; locked attributes cannot be removed and
 *   locked ranges cannot be overridden.
 *
 * 'cycle_defined' — default. Each cycle defines its own criteria and ranges
 *   independently. Template seeding still applies at creation time.
 */
export type CriteriaGovernanceMode = 'workflow_defined' | 'cycle_defined';

export interface WorkflowCriteriaGovernance {
  mode: CriteriaGovernanceMode;
  /**
   * Attributes controlled at workflow level. Only meaningful when
   * mode === 'workflow_defined'. Ignored (but harmless) in cycle_defined mode.
   */
  lockedAttributes?: WorkflowLockedAttribute[];
}

/** @deprecated Use TemplateRangePreset from cycleTemplate.ts instead. */
export interface WorkflowRangePreset {
  type: 'select' | 'number' | 'scale' | 'fibonacci' | 'tshirt' | 'custom';
  options?: (string | number)[];
  min?: number;
  max?: number;
  step?: number;
  labels?: Record<string, string>;
}

/** @deprecated Use TemplateParticipantPreset from cycleTemplate.ts instead. */
export interface WorkflowParticipantPreset {
  userId: string;
  displayName?: string;
  role?: 'facilitator' | 'contributor' | 'observer';
}

/** @deprecated Use TemplateGroupPreset from cycleTemplate.ts instead. */
export interface WorkflowGroupPreset {
  groupId: string;
  displayName?: string;
}

/**
 * Workflow configuration (JSONB). Doc 04 §10.1.
 *
 * Contains two categories of fields:
 *
 * 1. Orchestration Policy — runtime-enforced throughout the cycle lifecycle:
 *    allowed_session_modes, write-back policies, result_resolution, catchball_enabled
 *
 * 2. Cascade Defaults — flow down the inheritance chain (Workflow → Cycle → Track →
 *    Session); each level may override. Inspectable at the point of use:
 *    default_timing_mode, default_entry_mode, default_facilitator_id
 *
 * 3. template_id — optional reference to a CycleTemplate applied once at cycle
 *    creation. After creation the cycle owns those values; changes to the template
 *    do not affect existing cycles.
 *
 * @deprecated default_attributes, default_participants, default_groups
 *   These fields remain readable for backward compatibility with existing workflow
 *   JSONB data, but should not be written by new code. Use CycleTemplate instead.
 */
export interface WorkflowConfiguration {
  // --- Orchestration Policy ---

  /** Which session context modes are allowed. Default: all. */
  allowed_session_modes?: AllowedSessionMode[];
  /** How value judgments flow to items. Default: direct. */
  attribute_write_back?: AttributeWriteBack;
  /** How session results propagate. Default: to_track. */
  result_write_back?: ResultWriteBack;
  /** How outputs enter Catchball. Default: proposal_eligible. */
  governance_write_back?: GovernanceWriteBack;
  /** Default result resolution for tracks in this workflow. Default: latest_published. */
  result_resolution?: ResultResolutionStrategy;
  /** Whether track may override result_resolution. Default: false. */
  allow_track_resolution_override?: boolean;
  /** Whether Catchball loops are active. Default: true. */
  catchball_enabled?: boolean;

  // --- Catchball routing policy (canon 29 §6) ---

  /** When true, ball holders may Split / Fan out (Rollout). Default: false. */
  rollout_enabled?: boolean;
  /** How Split targets are chosen. Default: org_unit. */
  ball_split_unit?: 'disabled' | 'org_unit' | 'virtual_recipient' | 'track';
  /** Cap on child balls per Split. Default: 5. */
  ball_split_max_children?: number;
  /** Parent must Aggregate before pass-up when children exist. Default: true. */
  require_aggregate_before_pass_up?: boolean;
  /** Where conflicts resolve after a Split. Default: split_node. */
  resolution_target?:
    | 'split_node'
    | 'lowest_common_authority'
    | 'cycle_owner'
    | 'originating_requester';
  /**
   * Who may rewrite the remaining Path Instance spine.
   * Default: holder_remaining_spine.
   */
  path_adjust_policy?: 'none' | 'holder_remaining_spine' | 'cycle_owner_only';

  // --- Cascade Defaults ---

  /**
   * Default session run mode (Canon 23 §6.2). When set, wins over the legacy
   * default_timing_mode/default_entry_mode pair in cascade resolution.
   * Null = fall back to legacy pair, then system default (facilitator_live).
   */
  default_run_mode?: 'facilitator_live' | 'participant_live' | 'open' | null;
  /** @deprecated Use default_run_mode. Default session timing mode. 'live' | 'async'. Null = system default (live). */
  default_timing_mode?: 'live' | 'async' | null;
  /** @deprecated Use default_run_mode. Default data entry mode. 'facilitator' | 'participants'. Null = system default (facilitator). */
  default_entry_mode?: 'facilitator' | 'participants' | null;
  /** Default facilitator user ID. Null = none (set at cycle/track/session). */
  default_facilitator_id?: string | null;

  // --- Criteria governance ---

  /**
   * Workflow-level control over criteria definitions and value ranges.
   * When mode === 'workflow_defined', locked_attributes entries are enforced at
   * the store layer: locked criteria cannot be removed, locked ranges cannot be
   * overridden. Applies to every cycle that uses this workflow.
   *
   * Default: { mode: 'cycle_defined' } (each cycle defines independently).
   */
  criteria_governance?: WorkflowCriteriaGovernance | null;

  // --- Template reference ---

  /**
   * ID of the CycleTemplate to apply when a cycle is created with this workflow.
   * Applied once at cycle creation; has no ongoing effect.
   * Stored here as a convenience; authoritative reference is workflows.template_id column.
   */
  template_id?: string | null;

  // --- Deprecated initialization presets ---
  // These remain readable for backward compat with existing JSONB data.
  // New code must not write these; use CycleTemplate instead.

  /** @deprecated Use CycleTemplate.defaultAttributes instead. */
  default_attributes?: WorkflowAttributePreset[];
  /** @deprecated Use CycleTemplate.defaultParticipants instead. */
  default_participants?: WorkflowParticipantPreset[];
  /** @deprecated Use CycleTemplate.defaultGroups instead. */
  default_groups?: WorkflowGroupPreset[];

  [key: string]: unknown;
}

/** Resolved workflow config with canonical defaults applied. Used at runtime for gating. */
export interface EffectiveWorkflowConfig {
  allowedSessionModes: AllowedSessionMode[];
  attributeWriteBack: AttributeWriteBack;
  resultWriteBack: ResultWriteBack;
  governanceWriteBack: GovernanceWriteBack;
  resultResolution: ResultResolutionStrategy;
  allowTrackResolutionOverride: boolean;
  catchballEnabled: boolean;
  /**
   * Criteria and value-range governance policy.
   * Default: { mode: 'cycle_defined', lockedAttributes: [] }.
   */
  criteriaGovernance: WorkflowCriteriaGovernance;
  /** Set when sessionId provided: the session's context mode (constrains write-back etc.). */
  sessionContextMode?: SessionContextMode | null;
  /** Id of the workflow row used, or null if canonical default. */
  effectiveWorkflowId?: string | null;
}

export interface Workflow {
  id: string;
  /** null for unscoped/demo workflows when user is not in workspace_memberships. */
  workspaceId: string | null;
  name: string;
  description?: string | null;
  status: WorkflowStatus;
  configuration: WorkflowConfiguration;
  /**
   * Optional reference to a CycleTemplate applied once when a cycle is created
   * with this workflow. After creation the cycle owns the seeded values.
   */
  templateId?: string | null;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
}
